// Global Types
import type {
  $Children,
  $Component,
} from 'common.types';
import type {
  $Model,
  $ModelUpsertParams,
  $RegattaEntryResource,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  CrewmemberLanguage,
  RegattaProtestType,
} from 'common.data';

// Global Components
import * as React from 'react';
import Formsy from 'formsy-react';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  animationClass,
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegattaEntrySubTitle,
  getRegattaEntryTitle,
} from 'common.helpers/dist/browser';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  getRegattaGroupName,
} from '../../../helpers';

// Components
import Loader from '../../layout/Loader';
import SocialAuthButtons from '../../common/SocialAuthButtons';
import InputText from '../../form/InputText';
import InputTextarea from '../../form/InputTextarea';
import InputDate from '../../form/InputDate';
import InputTypeahead from '../../form/InputTypeahead';
import InputCheckbox from '../../form/InputCheckbox';
// import InputDrawing from '../../form/InputDrawing';
import InputLabel from '../../form/InputLabel';
import InputFile from '../../form/InputFile';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $AuthenticateAction,
} from '../../../redux/modules/auth/actions';
import type {
  $SetupUserAction,
} from '../../../redux/modules/main/actions';

type $OptionalProps = {
  children?: $Children;
};

export type $OwnProps = $OptionalProps & $Translate & {
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
};

export type $DispatchProps = {
  authenticate: $AuthenticateAction;
  setupUser: $SetupUserAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$RegattaEntryResource>;
  highlight: boolean;
  loading: boolean;
  regattaEntry: $RegattaEntryResource | null;
  visible: boolean;
};

class RegattaProtestDialogView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    children: undefined,
  };

  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      highlight: false,
      loading: false,
      regattaEntry: null,
      visible: false,
    };
  }

  async onValidSubmit(values: Record<string, unknown>): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      const initialParams: $ModelUpsertParams<'RegattaProtest'> = {
        AltInfo: false,
        AltInfoContent: null,
        CaseNo: null,
        Content: null,
        Decision: null,
        Drawing: null,
        File: null,
        Flag: false,
        FlagContent: null,
        Hailing: false,
        HailingContent: null,
        Image: null,
        PreContent: null,
        Protestee: '',
        ProtesteeGroup: null,
        ProtesteeRegattaEntryId: null,
        Protestor: '',
        ProtestorGroup: null,
        ProtestorRegattaEntryId: null,
        RaceDate: '',
        RaceNo: null,
        RegattaId: item.id,
        Rule: null,
        Type: RegattaProtestType.type1BoatToBoat,
        Witnesses: null,
      };

      await this.setStateAsync({
        loading: true,
      });

      const params = cleanFormValues<$ModelUpsertParams<'RegattaProtest'>>({
        ...initialParams,
        ...values,
        AltInfo: values.AltInfo || false,
        Flag: values.Flag || false,
        Hailing: values.Hailing || false,
      });

      await Services.Api.upsertRegattaProtestData(params);

      await this.setStateAsync({
        loading: false,
      });

      await this.toggle();
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);

      await this.setStateAsync({
        loading: false,
      });
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      auth,
      item,
    } = this.props;

    try {
      if (auth.user !== null) {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaEntryByCrewmember({
          RegattaId: item.id,
        });

        await this.setStateAsync({
          data,
        });

        if (data.length === 1) {
          this.setRegattaEntry(data[0]);
        }

        await this.setStateAsync({
          loading: false,
        });
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);

      await this.setStateAsync({
        loading: false,
      });
    }
  }

  setRegattaEntry(regattaEntry: $RegattaEntryResource): void {
    this.setState({
      regattaEntry,
    });
  }

  async toggle(): Promise<void> {
    const {
      visible,
    } = this.state;

    await this.setStateAsync({
      regattaEntry: null,
      visible: !visible,
    });

    if (visible === false) {
      await this.getData();
    }
  }

  childrenWithToggleHandler(): $Component<unknown> | null {
    const {
      children,
    } = this.props;

    if (children && React.isValidElement(children)) {
      return React.cloneElement(
        children,
        {
          // @ts-expect-error dialog
          onClick: () => this.toggle(),
        },
      );
    }

    return null;
  }

  renderCloseButton() {
    const {
      t,
    } = this.props;

    return (
      <button
        className={'btn btn_secondary btn_outlined uppercase'}
        onClick={() => this.toggle()}
        type={'button'}
      >
        {t('Close')}
      </button>
    );
  }

  renderAuth() {
    const {
      authenticate,
      setupUser,
    } = this.props;
    const {
      loading,
    } = this.state;

    return (
      <>
        <Loader
          className={'modal-body'}
          loading={loading === true}
        >
          <SocialAuthButtons
            onSuccessAction={async ({
              token,
              type,
            }) => {
              try {
                const {
                  value: user,
                } = await authenticate({
                  language: i18next.language as CrewmemberLanguage,
                  token,
                  type,
                });

                if (user !== null) {
                  await setupUser(user);
                }

                await this.getData();
              } catch (error) {
                ErrorLog.exception(error as Error);
              }
            }}
          />
        </Loader>
        <div className={'modal-footer flex justify-end'}>
          {this.renderCloseButton()}
        </div>
      </>
    );
  }

  renderList() {
    const {
      auth,
      item,
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <>
        <Loader
          className={'modal-body'}
          loading={loading === true}
        >
          {data.length > 0 ? data.map((dataItem: $RegattaEntryResource) => (
            <div
              className={'flex flex-row justify-between mb-3'}
              key={dataItem.id}
            >
              <div>
                <div className={'text-base'}>
                  {getRegattaEntryTitle({
                    bowNoEnabled: item.BowNoEnabled,
                    countryEnabled: item.CountryEnabled,
                    regattaEntry: dataItem,
                  })}
                </div>
                <small className={'leading-3 text-gray-600'}>
                  {getRegattaEntrySubTitle({
                    countryEnabled: item.CountryEnabled,
                    regattaEntry: dataItem,
                  })}
                </small>
              </div>
              <div className={'ml-3'}>
                <button
                  className={'btn btn_primary btn_outlined uppercase'}
                  onClick={() => this.setRegattaEntry(dataItem)}
                  type={'button'}
                >
                  {t('Use')}
                </button>
              </div>
            </div>
          )) : (
            <div>
              <h4 className={'mb-3'}>
                {`${t('Your account email')}: `}
                <strong>{auth.user ? auth.user.Email : ''}</strong>
              </h4>
              {t('No related to this email regatta entry crewmembers or coaches were found.')}
            </div>
          )}
        </Loader>
        <div className={'modal-footer flex justify-end'}>
          {this.renderCloseButton()}
        </div>
      </>
    );
  }

  renderForm() {
    const {
      item,
      t,
    } = this.props;
    const {
      highlight,
      loading,
      regattaEntry,
    } = this.state;

    const protestorGroup: string = getRegattaGroupName(_.get(
      regattaEntry,
      'RegattaGroup',
    )) || '';

    const now: moment.Moment = moment();
    const bowNoEnabled = item.BowNoEnabled;
    const countryEnabled = item.CountryEnabled;
    const protestor = getRegattaEntryTitle({
      bowNoEnabled,
      countryEnabled,
      regattaEntry,
    });

    return (
      <Formsy onValidSubmit={(values) => this.onValidSubmit(values)}>
        <Loader
          className={'modal-body'}
          loading={loading === true}
        >
          <div className={'flex flex-row mb-3'}>
            <div className={'basis-2/3'}>
              <InputDate
                highlight={highlight}
                label={t('Race date')}
                min={new Date()}
                name={'RaceDate'}
                required
                value={now.format('YYYY-MM-DD')}
              />
            </div>
            <div className={'basis-1/3'}>
              <InputText
                className={'ml-3'}
                highlight={highlight}
                label={t('Race No.')}
                name={'RaceNo'}
              />
            </div>
          </div>
          <div className={'flex flex-row mb-3'}>
            <div className={'basis-2/3'}>
              <InputText
                disabled
                highlight={highlight}
                label={t('Protestor')}
                name={'Protestor'}
                required
                value={protestor}
              />
            </div>
            <div className={'basis-1/3'}>
              <InputText
                className={'ml-3'}
                disabled
                highlight={highlight}
                label={t('Group')}
                name={'ProtestorGroup'}
                value={protestorGroup}
              />
            </div>
          </div>
          <div className={'flex flex-row mb-3'}>
            <div className={'basis-2/3'}>
              <InputTypeahead
                getOptions={async (search: string): Promise<Array<{
                  id: string;
                  Name: string;
                }>> => {
                  let output: Array<{
                    id: string;
                    Name: string;
                  }> = [];

                  try {
                    const request = {
                      RegattaId: item.id,
                      search,
                    };

                    const response = await Services.Api.findManyRegattaEntry(request);

                    output = response.map((responseItem) => {
                      const value = getRegattaEntryTitle({
                        bowNoEnabled,
                        countryEnabled,
                        regattaEntry: responseItem,
                      });

                      return {
                        id: value,
                        Name: value,
                      };
                    });
                  } catch (error) {
                    ErrorLog.exception(error as Error);
                  }

                  return output;
                }}
                highlight={highlight}
                label={t('Protestee')}
                name={'Protestee'}
                required
                value={''}
              />
            </div>
            <div className={'basis-1/3'}>
              <InputText
                className={'ml-3'}
                highlight={highlight}
                label={t('Group')}
                name={'ProtesteeGroup'}
              />
            </div>
          </div>
          <div className={'flex flex-row mb-3'}>
            <div className={'basis-2/3'}>
              <InputText
                highlight={highlight}
                label={t('Witnesses')}
                name={'Witnesses'}
              />
            </div>
            <div className={'basis-1/3'}>
              <InputText
                className={'ml-3'}
                highlight={highlight}
                label={t('Rule broken')}
                name={'Rule'}
              />
            </div>
          </div>
          <div>
            <InputLabel
              className={''}
              label={t('Informing protestee')}
              name={'InformingProtestee'}
            />
            <div id={'InformingProtestee'}>
              <div className={'flex flex-row mb-3'}>
                <div
                  className={'basis-1/3 pt-3'}
                  title={t('By hailing')}
                >
                  <InputCheckbox
                    label={t('By hailing')}
                    name={'Hailing'}
                  />
                </div>
                <div className={'basis-2/3'}>
                  <InputText
                    highlight={highlight}
                    name={'HailingContent'}
                    placeholder={`${t('When')}?`.toUpperCase()}
                  />
                </div>
              </div>
              <div className={'flex flex-row mb-3'}>
                <div
                  className={'basis-1/3 pt-3'}
                  title={t('By displaying a red flag')}
                >
                  <InputCheckbox
                    label={t('By red flag')}
                    name={'Flag'}
                  />
                </div>
                <div className={'basis-2/3'}>
                  <InputText
                    highlight={highlight}
                    name={'FlagContent'}
                    placeholder={`${t('When')}?`.toUpperCase()}
                  />
                </div>
              </div>
              <div className={'flex flex-row mb-3'}>
                <div
                  className={'basis-1/3 pt-3'}
                  title={t('By informing her in some alternative way')}
                >
                  <InputCheckbox
                    label={t('By alt. way')}
                    name={'AltInfo'}
                  />
                </div>
                <div className={'basis-2/3'}>
                  <InputText
                    highlight={highlight}
                    name={'AltInfoContent'}
                    placeholder={t('Give details').toUpperCase()}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={'mb-3'}>
            <InputText
              highlight={highlight}
              label={t('Where and when the incident occurred')}
              name={'PreContent'}
              required
            />
          </div>
          <div className={'mb-3'}>
            <InputTextarea
              highlight={highlight}
              label={t('Incident description')}
              name={'Content'}
              required
              rows={8}
            />
          </div>
          {/* <div className="mb-3">
            <InputDrawing
              height={360}
              label={t('Incident drawing')}
              name="Drawing"
              width={368}
            />
          </div> */}
          <div className={'mb-3'}>
            <InputFile
              highlight={highlight}
              image
              label={t('Incident drawing image')}
              name={'Image'}
            />
          </div>
        </Loader>
        <div className={'modal-footer flex justify-end'}>
          <button
            className={'btn btn_primary btn_outlined uppercase ml-auto mr-1'}
            onClick={() => this.setState({
              highlight: true,
            })}
            type={'submit'}
          >
            {t('Submit')}
          </button>
          {this.renderCloseButton()}
        </div>
      </Formsy>
    );
  }

  renderContent() {
    const {
      regattaEntry,
    } = this.state;

    return regattaEntry === null ? this.renderList() : this.renderForm();
  }

  render() {
    const {
      auth,
      t,
    } = this.props;
    const {
      visible,
    } = this.state;

    return (
      <>
        {this.childrenWithToggleHandler()}
        <div className={`modal bg-gray-300 bg-opacity-50 ${visible ? 'active' : ''} ${animationClass('fadeIn')}`}>
          <div className={'modal-dialog max-w-md mx-auto'}>
            <div className={'modal-content w-full h-full'}>
              <div className={'modal-header'}>
                <h3 className={'h3'}>
                  {t('Protest')}
                </h3>
                <button
                  className={'close la la-times'}
                  onClick={() => this.toggle()}
                  type={'button'}
                />
              </div>
              {auth.user !== null ? this.renderContent() : this.renderAuth()}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default RegattaProtestDialogView;
